/* eslint-disable complexity */
import React, {useDebugValue, useRef, useEffect} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks/useSearchFormContext';
import {useTrackingContext} from '../../../hooks/useTrackingContext';

import {saveChildrenAgesTripCookie, submitForm} from '../../../utils/form';
import {
    getAddedChildAgeData,
    getGuestsFieldInputLabel,
    getGuestsFieldAdultsInputLabel,
    getGuestsFieldAgeSelectTitle,
    getGuestsFieldChildrenInputLabel,
    getGuestsFieldBedroomsInputLabel,
    shouldChildrenAgesCookieUpdate,
    shouldGuestsFieldAutoSubmitOnClose,
    setChildrenAgeSelectorData,
    showGuestsInputFieldError,
    showGuestsInputIcon
} from '../../../utils/guests';
import {runGuestPickerBedroomsExperiment, runGuestPickerAgeofChildrenExperiment} from '../../../utils/experiments';

import {SearchIcon} from '../../SearchIcon/SearchIcon';

const classNamespace = 'search-form';

const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldClassNameContrast = `${classNamespace}__input-field-contrast`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const detailsinputFieldClassNameContrast = `${classNamespace}__details-input-field-contrast`;
const guestsDetailsInputFieldClassName = `${classNamespace}__guests-details-input-field`;

const guestsPickerClassName = `${classNamespace}__guests-picker`;

function useComponent() {
    const {
        systemOptions,
        mediaSettings,
        mediaSettings: {
            isMobile
        },
        abacusStore,
        edapOptions,
        latestSearchValues,
        formActions,
        formEvents,
        formOptions,
        formSettings,
        destinationActions,
        destinationActions: {
            setIsTypeaheadInputFocus
        },
        latestDestinationValues,
        datesActions: {
            setIsOpen: setDatesIsOpen
        },
        latestDatesValues,
        guestsActions: {
            setIsOpen: setGuestsIsOpen,
            setCurrentGuestsValuesLatest,
            setLatestGuestsValues,
            setAllGuestsValuesDefault
        },
        guestsEvents: {
            onAgeChange,
            onChange,
            onClear,
            onClose,
            onExit,
            onOpen
        },
        guestsOptions,
        guestsOptions: {
            actionLocation,
            adultsMax,
            adultsMin,
            ageOptions,
            bedroomsMax,
            bedroomsMin,
            centered,
            childrenMax,
            childrenMin,
            disableTextInput,
            fadeOut,
            fadeOutDuration,
            id,
            inputIconName,
            isAriaRoleExperimentEnabled,
            name,
            placement,
            positionIconRight,
            showAgeRanges,
            showAgeSelect,
            showBedroomsOption,
            showClearButton,
            showContinue,
            showPetOption,
            wdioPrefix
        },
        guestsSettings: {
            disableButton,
            isOpen,
            readOnly
        },
        guestsTranslations,
        guestsTranslations: {
            label,
            modalTitle,
            ageSelectSubtitle,
            petLabel,
            petNoLabel,
            petYesLabel,
            petMessage,
            applyButtonLabel,
            clearButtonLabel,
            continueButtonLabel
        },
        latestGuestsValues,
        latestGuestsValues: {
            adultsCount,
            ageData,
            childrenCount,
            bedroomsCount,
            petIncluded
        }
    } = useSearchFormContext();

    const {
        isColourContrastAndLabels
    } = formOptions;

    const trackingContext = useTrackingContext();

    const className = (isColourContrastAndLabels && !showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues)) ?
        classNames(fieldClassName, inputFieldClassNameContrast, {
            [inputFieldErrorClassName]: showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues)
        }, detailsFieldClassName, detailsinputFieldClassNameContrast, guestsDetailsInputFieldClassName) :
        classNames(fieldClassName, inputFieldClassName, {
            [inputFieldErrorClassName]: showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues)
        }, detailsFieldClassName, detailsInputFieldClassName, guestsDetailsInputFieldClassName);

    const pickerClassName = classNames(guestsPickerClassName);

    const inputIcon = showGuestsInputIcon(formOptions, guestsOptions) && (
        <SearchIcon name={inputIconName}/>
    ) || null;

    const guestLabel = getGuestsFieldInputLabel(guestsTranslations, latestGuestsValues, showBedroomsOption);

    const adultsLabel = getGuestsFieldAdultsInputLabel(guestsTranslations, latestGuestsValues);
    const childrenLabel = getGuestsFieldChildrenInputLabel(guestsTranslations, latestGuestsValues);
    const bedroomsLabel = getGuestsFieldBedroomsInputLabel(guestsTranslations, latestGuestsValues);
    const ageSelectTitle = getGuestsFieldAgeSelectTitle(guestsTranslations, latestGuestsValues);

    const formSuffixElement = petIncluded && (
        <p className="text-center text-muted">{petMessage}</p>
    ) || null;

    const prevChildrenCountRef = useRef(childrenCount);

    useEffect(() => {
        prevChildrenCountRef.current = childrenCount;
    }, [childrenCount]);

    function getPrevChildrenCount() {
        return prevChildrenCountRef.current;
    }

    function setLatestChildrenAgeData() {
        if (childrenCount === ageData.length) {
            return;
        }
        let updatedAgeSelectorData = [...ageData];
        if (childrenCount > ageData.length) {
            let diff = childrenCount - ageData.length;
            let ageSelectorData = getAddedChildAgeData(ageOptions);
            for (let i = 0; i < diff; i++) {
                updatedAgeSelectorData.push(ageSelectorData);
            }
        } else {
            updatedAgeSelectorData = updatedAgeSelectorData.splice(0, childrenCount);
        }
        setLatestGuestsValues({ageData: updatedAgeSelectorData});
    }

    function handleChange(...args) {
        const [
            changedKey,
            changedValue
        ] = args;

        const changedGuestsValues = {
            [changedKey]: changedValue
        };

        if (changedKey === 'childrenCount' && showAgeSelect) {
            changedGuestsValues.ageData = setChildrenAgeSelectorData({prevValue: getPrevChildrenCount(), nextValue: changedValue}, {ageData, ageOptions});
        }

        setLatestGuestsValues(changedGuestsValues);
        onChange(...args);
    }

    function handleAgeChange(...args) {
        const [
            changedKey,
            changedValue
        ] = args;
        const changedGuestsValues = {
            ageData: ageData.map((_value, _index) =>
                changedKey === _index
                    ? {value: changedValue}
                    : {...ageData[_index]}
            )
        };
        setLatestGuestsValues(changedGuestsValues);
        onAgeChange(...args);
    }

    function handleClear(...args) {
        setAllGuestsValuesDefault();

        onClear(...args);
    }

    function handleClose(...args) {
        setCurrentGuestsValuesLatest();

        setGuestsIsOpen(false);

        const isGuestsFieldAutoSubmitOnClose = shouldGuestsFieldAutoSubmitOnClose(mediaSettings, formOptions, guestsOptions, abacusStore, systemOptions, latestDestinationValues, latestDatesValues);

        if (isGuestsFieldAutoSubmitOnClose) {
            submitForm(trackingContext, systemOptions, abacusStore, edapOptions, latestSearchValues, formActions, formEvents, formSettings, destinationActions, latestDestinationValues, latestDatesValues, latestGuestsValues);
        }

        if (!isGuestsFieldAutoSubmitOnClose && shouldChildrenAgesCookieUpdate(actionLocation)) {
            saveChildrenAgesTripCookie(childrenCount, ageData);
        }

        onClose(...args);
    }

    function handleExit(...args) {
        if (shouldChildrenAgesCookieUpdate(actionLocation)) {
            saveChildrenAgesTripCookie(childrenCount, ageData);
        }
        setCurrentGuestsValuesLatest();
        setGuestsIsOpen(false);

        onExit(...args);
    }

    function handleOpen(...args) {
        setIsTypeaheadInputFocus(false);
        setDatesIsOpen(false);
        setGuestsIsOpen(true);
        runGuestPickerBedroomsExperiment({shouldLogExperiment: true}, abacusStore);
        runGuestPickerAgeofChildrenExperiment({shouldLogExperiment: true}, abacusStore);
        setLatestChildrenAgeData();

        onOpen(...args);
    }

    const result = {
        className,
        actionLocation,
        adultsCount,
        adultsLabel,
        adultsMax,
        adultsMin,
        ageData,
        ageOptions,
        ageSelectSubtitle,
        ageSelectTitle,
        applyButtonLabel,
        bedroomsCount,
        bedroomsLabel,
        bedroomsMax,
        bedroomsMin,
        centered,
        childrenCount,
        childrenLabel,
        childrenMax,
        childrenMin,
        clearButtonLabel,
        continueButtonLabel,
        disableButton,
        disableTextInput,
        fadeOut,
        fadeOutDuration,
        formSuffixElement,
        guestLabel,
        id,
        inputIcon,
        isAriaRoleExperimentEnabled,
        isMobile,
        isOpen,
        label,
        modalTitle,
        name,
        onAgeChange: handleAgeChange,
        onChange: handleChange,
        onClear: handleClear,
        onClose: handleClose,
        onExit: handleExit,
        onOpen: handleOpen,
        petIncluded,
        petLabel,
        petNoLabel,
        petYesLabel,
        pickerClassName,
        placement,
        positionIconRight,
        readOnly,
        showAgeRanges,
        showAgeSelect,
        showBedroomsOption,
        showClearButton,
        showContinue,
        showPetOption,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
